import { ApiResponseType, selectExperimentVariant } from '../../shared';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OnBoardingFlowType, OnBoardingScreenOptions } from '@mindly/ui-components';
import { fetchNewDataOnBoardingFlow, fetchOnBoardingFlow } from './thunk';
import { calculateProgress, getFiltrationAnswerIds } from './utils';
import { ILocalizedAreasOfWorkByClientProblem } from 'src/pages/types';

type InitialStateType = {
  isLoading: boolean;
  flowData: OnBoardingFlowType | null;
  progressInfo: {
    progress: number;
    totalCount: number;
    currentCount: number;
  };
  error: string | null;
  selectedAnswers: Record<string, string[]>;
  screensHistory: string[];
  currentScreenId: string | null;
  isTesting: boolean;
  selectedExperimentVariant?: { name: string; id: string } | null;
  onboarding_key: string | null;
  userEmail: string | null;
  useAdditionalPromotionalCodeDiscount?: boolean | null;
  localizedAreasOfWorkByClientProblem: ILocalizedAreasOfWorkByClientProblem[];
  goalScreenData: {
    currentGoalScreenCount: number;
  };
};

const initialState: InitialStateType = {
  isLoading: false,
  flowData: null,
  progressInfo: {
    progress: 0,
    totalCount: 0,
    currentCount: 0,
  },
  error: null,
  selectedAnswers: {},
  screensHistory: [],
  currentScreenId: null,
  isTesting: false,
  selectedExperimentVariant: undefined,
  onboarding_key: null,
  userEmail: null,
  useAdditionalPromotionalCodeDiscount: null,
  localizedAreasOfWorkByClientProblem: [],
  goalScreenData: {
    currentGoalScreenCount: localStorage.getItem('currentGoalScreenCount')
      ? Number(localStorage.getItem('currentGoalScreenCount'))
      : 0,
  },
};

export const onBoardingFlowSlice = createSlice({
  name: 'onBoardingFlow',
  initialState: sessionStorage.getItem('onBoardingFlowState')
    ? {
        ...JSON.parse(sessionStorage.getItem('onBoardingFlowState') as string),
        selectedExperimentVariant: undefined,
        useAdditionalPromotionalCodeDiscount: null,
        goalScreenData: {
          currentGoalScreenCount: localStorage.getItem('currentGoalScreenCount')
            ? Number(localStorage.getItem('currentGoalScreenCount'))
            : 0,
        },
      }
    : initialState,
  reducers: {
    setOnBoardingKey: (state, action: PayloadAction<string>) => {
      state.onboarding_key = action.payload;
    },
    setProgress: (state, action: PayloadAction<{ screenId: string; currentGoalScreenCount?: number }>) => {
      const calculatedProgressInfo = calculateProgress(
        state.flowData?.screens ?? {},
        action.payload.screenId,
        action.payload.currentGoalScreenCount ?? state.goalScreenData.currentGoalScreenCount ?? 0
      );

      state.progressInfo = calculatedProgressInfo;
    },
    setCurrentScreenId: (state, action: PayloadAction<string | null>) => {
      state.currentScreenId = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setAnswer: (state, action: PayloadAction<{ screenId: string; answerIds: string[] }>) => {
      const currentScreen = state.flowData?.screens[action.payload.screenId];

      if ([OnBoardingScreenOptions.GOAL, OnBoardingScreenOptions.LIST_LOADER].includes(currentScreen?.type)) {
        state.selectedAnswers[action.payload.screenId] = action.payload.answerIds;
      } else if (currentScreen && 'options' in currentScreen) {
        state.selectedAnswers[action.payload.screenId] = getFiltrationAnswerIds(
          action.payload.answerIds,
          currentScreen.options
        );
      }
    },
    updateScreensHistory: (state, action: PayloadAction<string>) => {
      const lastScreenId =
        state.screensHistory.length > 0 ? state.screensHistory[state.screensHistory.length - 1] : null;
      const screenId = action.payload;

      if (lastScreenId === screenId) return state;

      const existingIndex = state.screensHistory.indexOf(screenId);
      if (existingIndex !== -1) {
        state.screensHistory = state.screensHistory.slice(0, existingIndex + 1);
      } else {
        state.screensHistory.push(screenId);
      }
    },
    isTestingMode: (state, action: PayloadAction<boolean>) => {
      state.isTesting = action.payload;
    },
    setSelectedExperimentVariant: (state, action: PayloadAction<{ name: string; id: string }>) => {
      state.selectedExperimentVariant = action.payload;
    },
    setInitialFlowData: () => {
      return { ...initialState };
    },
    setUserEmail: (state, action: PayloadAction<string | null>) => {
      state.userEmail = action.payload;
    },
    setUseAdditionalPromotionalCodeDiscount: (state, action: PayloadAction<boolean | null>) => {
      state.useAdditionalPromotionalCodeDiscount = action.payload;
    },
    setLocalizedAreasOfWorkByClientProblem: (state, action: PayloadAction<ILocalizedAreasOfWorkByClientProblem[]>) => {
      state.localizedAreasOfWorkByClientProblem = action.payload;
    },
    setGoalScreenData: (
      state,
      action: PayloadAction<{
        currentGoalScreenCount: number;
      }>
    ) => {
      localStorage.setItem('currentGoalScreenCount', action.payload.currentGoalScreenCount.toString());

      state.goalScreenData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOnBoardingFlow.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.flowData = null;
    });
    builder.addCase(fetchOnBoardingFlow.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(
      fetchOnBoardingFlow.fulfilled,
      (state, action: PayloadAction<ApiResponseType<OnBoardingFlowType>>) => {
        return {
          ...initialState,
          flowData: action.payload.data,
          error: action.payload.error ? action.payload.error[0].msg : null,
          onboarding_key: state.onboarding_key,
          localizedAreasOfWorkByClientProblem: state.localizedAreasOfWorkByClientProblem,
          selectedExperimentVariant:
            action.payload.data?.experiment?.enabled === true
              ? selectExperimentVariant({
                  variants: [
                    {
                      name: `${action.payload.data.experiment.name}_control`,
                      id: 'control',
                    },
                    ...action.payload.data.experiment.variants.map((variant) => ({
                      name: variant.name,
                      id: variant.id,
                    })),
                  ],
                  persistKey: action.payload.data.experiment.id,
                })
              : null,
        };
      }
    );
    builder.addCase(fetchNewDataOnBoardingFlow.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.flowData = null;
    });
    builder.addCase(fetchNewDataOnBoardingFlow.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(
      fetchNewDataOnBoardingFlow.fulfilled,
      (state, action: PayloadAction<ApiResponseType<OnBoardingFlowType>>) => {
        return {
          ...state,
          isLoading: false,
          flowData: action.payload.data,
          error: action.payload.error ? action.payload.error[0].msg : null,
          onboarding_key: state.onboarding_key,
          localizedAreasOfWorkByClientProblem: state.localizedAreasOfWorkByClientProblem,
          selectedExperimentVariant:
            action.payload.data?.experiment?.enabled === true
              ? selectExperimentVariant({
                  variants: [
                    {
                      name: `${action.payload.data.experiment.name}_control`,
                      id: 'control',
                    },
                    ...action.payload.data.experiment.variants.map((variant) => ({
                      name: variant.name,
                      id: variant.id,
                    })),
                  ],
                  persistKey: action.payload.data.experiment.id,
                })
              : null,
        };
      }
    );
  },
});

export default onBoardingFlowSlice.reducer;

export const {
  setProgress,
  setErrorMessage,
  setAnswer,
  updateScreensHistory,
  setCurrentScreenId,
  isTestingMode,
  setOnBoardingKey,
  setSelectedExperimentVariant,
  setInitialFlowData,
  setUserEmail,
  setUseAdditionalPromotionalCodeDiscount,
  setLocalizedAreasOfWorkByClientProblem,
  setGoalScreenData,
} = onBoardingFlowSlice.actions;
